/**
 * LAB-FE-1 B2 — THE TEST REGISTER (approved prototype
 * `docs/design/mockups/prototipo-lab-v1.html`, `registerPage`; D20.5).
 *
 * One read (`GET /projects/{p}/test-requests`) serves the rows, the bucket
 * counts and the lines that still require a request — the screen recomposes no
 * rule. Six counters are the filters: All requests · Test required · not
 * requested · Awaiting results · Awaiting engineering review · Returned to
 * laboratory · Accepted. The "Test required" bucket is a different table: it
 * lists LINES (obligations), not requests, and it is where a request is raised
 * (the only other door is the ITC line itself).
 *
 * DELTA 4 ITEM 9. A reader the register excludes (the client representative)
 * is answered with an empty register and a 200, never a 403 — so there is no
 * "not allowed" branch here, only the ordinary empty state in neutral words.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import ResizableTable, { ResizableTh, TD_CLASS, TD_NOWRAP } from '../components/ui/ResizableTable';
import { Alert, btn, ORANGE_HEAD, why as WHY } from '../components/handover/v2/HoBits';
import { StateChips, TrChip } from '../components/test-register/TrBits';
import RequestTestDialog from '../components/test-register/RequestTestDialog';
import {
  TWO_FAILS, coversLines, coversShort, fDay, itcLine,
} from '../components/test-register/labView';
import { serverMessage } from '../lib/handoverView';
import { getProject, getTestRegister } from '../lib/api';

/**
 * The external-request family answers 4xx in THREE shapes: `detail` as a plain
 * string, `detail` as a dict `{code, status, message}`, and FastAPI's own
 * validation list. Reading only the first would print "[object Object]" at the
 * user for a whole half of the family's errors. (Imported by other screens.)
 */
export const apiErrorMessage = (err, fallback = 'Something went wrong') => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const first = detail.find((e) => typeof e?.msg === 'string' && e.msg.trim());
    if (first) return first.msg;
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string'
      && detail.message.trim()) {
    return detail.message;
  }
  return fallback;
};

/** The six counters, in the prototype's order. `need` is the lines table;
 *  the others are the server's buckets. */
const BUCKETS = [
  { k: 'all', l: 'All requests', count: 'all' },
  { k: 'need', l: 'Test required · not requested', count: 'requires_request' },
  { k: 'awaiting_results', l: 'Awaiting results', count: 'awaiting_results' },
  { k: 'awaiting_review', l: 'Awaiting engineering review', count: 'awaiting_review' },
  { k: 'returned', l: 'Returned to laboratory', count: 'returned' },
  { k: 'accepted', l: 'Accepted', count: 'accepted' },
];
const BUCKET_KEYS = BUCKETS.map((b) => b.k);

/** LAB-BE-2 B1 — the server's `actions.can_review` on a row waiting for
 *  review: the reader's own action (one producer with the request page). */
const isMyReview = (r) => r?.state === 'awaiting_review' && !!r?.actions?.can_review;

/** The buckets whose read leaves the awaiting-review rows out: there, the
 *  counter's "your action" needs the awaiting-review bucket read beside it. */
const HIDES_REVIEW = ['awaiting_results', 'returned', 'accepted'];

/** "Why it counts here", from the server's `why` and `previous_tr` (LAB-BE-2
 *  B1: `previous_tr.cancelled.reason` quoted when the request was cancelled). */
const whyText = (line) => {
  const prev = line.previous_tr;
  if (line.why === 'failed') {
    return `previous request${prev?.request_number ? ` ${prev.request_number}` : ''} · Accepted · Fail — retest needed`;
  }
  if (line.why === 'cancelled') {
    const reason = prev?.cancelled?.reason;
    return `previous request${prev?.request_number ? ` ${prev.request_number}` : ''} cancelled${reason ? ` — “${reason}”` : ''}`;
  }
  return 'no request yet';
};

/** The row's second line under the test name (prototype `sub`); a cancelled
 *  row says why and who (LAB-BE-2 B1 `cancelled {reason, by}`). */
const testSub = (r) => {
  if (r.retest_of?.request_number) return `Retest of ${r.retest_of.request_number}`;
  if (r.state === 'accepted_fail') {
    return r.ncr_number ? `NCR required → ${r.ncr_number} open` : 'NCR required';
  }
  if (r.state === 'cancelled') {
    const c = r.cancelled;
    if (!c?.reason) return 'Cancelled';
    return `Cancelled: ${c.reason}${c.by ? ` — ${c.by}` : ''}`;
  }
  return r.standard || '';
};

/**
 * LAB-BE-2 B1 — the retest door of an Accepted · Fail row (`actions.can_request`,
 * the ORIGIN LINE's own answer). The dialog is fed by that line as the same read
 * serves it in `lines_requiring_request` (its `retest_of`, its reason rule);
 * a line outside that list (none today) falls back to the row's origin cover:
 * a retest of this request, with its reason.
 */
const retestFrom = (r, lines) => {
  const covers = Array.isArray(r.covers) ? r.covers : [];
  const origin = covers.find((c) => c.is_origin) || covers[0] || {};
  const served = lines.find((l) => l.itc_id === origin.itc_id && l.item_id === origin.item_id);
  if (served) return served;
  return {
    ...origin,
    test_name: r.test_name,
    standard: r.standard,
    acceptance: r.acceptance,
    why: 'failed',
    retest_of: r.id,
    previous_tr: { id: r.id, request_number: r.request_number, state: r.state },
    retest_reason_required: true,
  };
};

export default function TestRegisterPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const asked = params.get('bucket') || 'all';
  const bucket = BUCKET_KEYS.includes(asked) ? asked : 'all';

  const [project, setProject] = useState(null);
  const [reg, setReg] = useState(null);
  const [mine, setMine] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [requesting, setRequesting] = useState(null);

  useEffect(() => {
    let off = false;
    getProject(projectId).then((res) => { if (!off) setProject(res?.data || null); }).catch(() => {});
    return () => { off = true; };
  }, [projectId]);

  const load = useCallback(async () => {
    try {
      const hides = HIDES_REVIEW.includes(bucket);
      const [data, review] = await Promise.all([
        getTestRegister(projectId, { bucket: bucket === 'need' ? undefined : bucket }),
        hides
          ? getTestRegister(projectId, { bucket: 'awaiting_review' }).catch(() => null)
          : null,
      ]);
      setReg(data || null);
      const seen = hides ? review?.items : data?.items;
      setMine(Array.isArray(seen) && seen.some(isMyReview));
      setLoadError(null);
    } catch (err) {
      setLoadError(serverMessage(err, 'The Test Register could not be loaded.'));
    }
  }, [projectId, bucket]);

  useEffect(() => { load(); }, [load]);

  const pick = (k) => {
    const next = new URLSearchParams(params);
    if (k === 'all') next.delete('bucket'); else next.set('bucket', k);
    setParams(next, { replace: true });
  };

  const counts = reg?.counts || {};
  const items = Array.isArray(reg?.items) ? reg.items : [];
  const lines = Array.isArray(reg?.lines_requiring_request) ? reg.lines_requiring_request : [];
  const total = Number(counts.all) || 0;
  const openTr = (id) => navigate(`/projects/${projectId}/test-requests/${id}`);

  return (
    <Layout>
      <div className="max-w-[1360px] mx-auto px-4 md:px-6 pt-5 pb-24" data-testid="test-register">
        <div className="flex items-start gap-3 mb-4">
          <button
            type="button"
            className="text-slate-400 hover:text-white mt-1"
            onClick={() => navigate(`/projects/${projectId}`)}
            data-testid="test-register-back"
            aria-label="Back to the project"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="m-0 mb-0.5 text-[22px] font-bold text-slate-50 tracking-tight" data-testid="test-register-title">
              Test Register
            </h1>
            <p className="m-0 text-[13px] text-slate-400" data-testid="test-register-sub">
              {project?.name ? `${project.name} · ` : ''}
              {`${total} test request${total === 1 ? '' : 's'} · a request is raised from an ITC line that requires a test; the register follows it up`}
            </p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap mb-3.5 items-center" data-testid="tr-buckets">
          {BUCKETS.map((b) => (
            <button
              key={b.k}
              type="button"
              onClick={() => pick(b.k)}
              aria-pressed={bucket === b.k}
              data-testid={`tr-bucket-${b.k}`}
              className={`rounded-full border px-3.5 py-1.5 text-[12.5px] font-semibold ${bucket === b.k
                ? 'bg-orange-500/15 border-orange-500/50 text-orange-300'
                : 'bg-transparent border-slate-700 text-slate-300 hover:border-slate-500'}`}
            >
              <b className="mr-1.5" data-testid={`tr-count-${b.k}`}>{Number(counts[b.count]) || 0}</b>
              {b.l}
              {b.k === 'awaiting_review' && mine ? (
                <span className="ml-1.5 inline-block rounded-full px-[7px] py-px text-[11px] font-bold bg-orange-500/15 border border-orange-500/50 text-orange-300"
                  data-testid="tr-your-action">
                  your action
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {loadError ? <Alert tone="bad" testId="tr-load-error" className="mb-3">{loadError}</Alert> : null}

        {!reg && !loadError ? (
          <p className="text-[12.5px] text-slate-500" data-testid="tr-loading">Loading…</p>
        ) : null}

        {reg && bucket === 'need' ? (
          <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-3.5" data-testid="tr-need">
            <div className="flex items-center gap-2 mb-2">
              <h4 className={`m-0 ${ORANGE_HEAD}`}>Test required · not requested</h4>
              <span className="text-[11px] text-slate-500">{lines.length}</span>
            </div>
            {lines.length ? (
              <ResizableTable testId="tr-need-table">
                <thead>
                  <tr>
                    <ResizableTh>ITC · line</ResizableTh>
                    <ResizableTh>Asset</ResizableTh>
                    <ResizableTh>Test required</ResizableTh>
                    <ResizableTh>Acceptance (from ITP)</ResizableTh>
                    <ResizableTh>Why it counts here</ResizableTh>
                    <ResizableTh resizable={false}>{''}</ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={`${l.itc_id}:${l.item_id}`} data-testid={`tr-need-${l.item_id}`}>
                      <td className={TD_NOWRAP}>
                        <button type="button" className={btn.text} data-testid={`tr-need-open-${l.item_id}`}
                          onClick={() => navigate(`/structured-itcs/${l.itc_id}?item=${l.item_id}`)}>
                          {l.item_no ? itcLine(l.itc_number, l.item_no) : 'Open the ITC line'}
                        </button>
                      </td>
                      <td className={TD_NOWRAP}>
                        <span className="font-mono text-orange-500 font-bold text-[12px]">{l.asset_number || '—'}</span>
                        <span className="block text-slate-500 text-[11px]">{l.asset_name || ''}</span>
                      </td>
                      <td className={`${TD_CLASS} text-slate-50 max-w-[60ch]`}>
                        {l.test_name || '—'}
                        {l.standard ? <span className="block text-slate-500 text-[11px]">{l.standard}</span> : null}
                      </td>
                      <td className={`${TD_CLASS} max-w-[60ch]`}>{l.acceptance || '—'}</td>
                      <td className={`${TD_CLASS} text-amber-400 max-w-[60ch]`} data-testid={`tr-need-why-${l.item_id}`}>{whyText(l)}</td>
                      <td className={`${TD_NOWRAP} text-right`}>
                        {l.can_request ? (
                          <button type="button" className={`${btn.primary} ${btn.sm}`}
                            data-testid={`tr-need-request-${l.item_id}`}
                            onClick={() => setRequesting(l)}>
                            {l.why === 'failed' ? 'Request retest' : 'Request Test'}
                          </button>
                        ) : l.why_not === 'ncr_required' ? (
                          <span className="text-[12px] text-red-300 whitespace-normal block max-w-[34ch]" data-testid={`tr-need-why-not-${l.item_id}`}>
                            {TWO_FAILS}
                          </span>
                        ) : l.executing_party?.company_name ? (
                          <span className="text-[12px] text-slate-500 whitespace-nowrap" data-testid={`tr-need-ep-${l.item_id}`}>
                            {`${l.executing_party.company_name} requests`}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>
            ) : (
              <p className={WHY} data-testid="tr-need-empty">Every test obligation has a live or accepted request.</p>
            )}
            <p className="text-[11px] text-slate-500 mt-1.5 mb-0">
              A line counts here while it has a test obligation and no live or accepted request — whether it never had one or its request was cancelled.
            </p>
          </div>
        ) : null}

        {reg && bucket !== 'need' ? (
          <div className="bg-slate-900 border border-slate-800 rounded-xl px-4 py-3.5" data-testid="tr-requests">
            <div className="flex items-center gap-2 mb-2">
              <h4 className={`m-0 ${ORANGE_HEAD}`}>Test requests</h4>
              <span className="text-[11px] text-slate-500" data-testid="tr-requests-count">{items.length}</span>
            </div>
            {items.length ? (
              <ResizableTable testId="test-register-table">
                <thead>
                  <tr>
                    <ResizableTh>TR</ResizableTh>
                    <ResizableTh>Test</ResizableTh>
                    <ResizableTh>Assets</ResizableTh>
                    <ResizableTh>Laboratory</ResizableTh>
                    <ResizableTh>Required</ResizableTh>
                    <ResizableTh>Status</ResizableTh>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr
                      key={r.id}
                      className="cursor-pointer hover:bg-white/[0.02]"
                      onClick={() => openTr(r.id)}
                      data-testid={`tr-row-${r.id}`}
                    >
                      <td className={TD_NOWRAP}>
                        <span className="font-mono text-orange-500 font-bold text-[12px]">{r.request_number || '—'}</span>
                      </td>
                      <td className={`${TD_CLASS} text-slate-50 max-w-[60ch]`}>
                        {r.test_name || '—'}
                        {testSub(r) ? <span className="block text-slate-500 text-[11px]" data-testid={`tr-row-sub-${r.id}`}>{testSub(r)}</span> : null}
                      </td>
                      <td className={`${TD_CLASS} max-w-[60ch]`} data-testid={`tr-row-assets-${r.id}`}>
                        {coversShort(r.covers)}
                        {coversLines(r.covers) ? <span className="block text-slate-500 text-[11px]">{coversLines(r.covers)}</span> : null}
                      </td>
                      <td className={TD_NOWRAP}>{r.laboratory?.name || '—'}</td>
                      <td className={TD_NOWRAP}>{r.state === 'cancelled' ? '—' : fDay(r.required_date)}</td>
                      <td className={TD_NOWRAP}>
                        <span className="inline-flex flex-wrap gap-1.5 items-center">
                          <StateChips state={r.state} overdue={r.overdue} requiredDate={r.required_date} testId={`tr-status-${r.id}`} />
                          {isMyReview(r) ? <TrChip tone="mine" testId={`tr-your-review-${r.id}`}>Your review</TrChip> : null}
                          {r.state === 'accepted_fail' && r.actions?.can_request ? (
                            <button type="button" className={`${btn.primary} ${btn.sm}`}
                              data-testid={`tr-row-retest-${r.id}`}
                              onClick={(e) => { e.stopPropagation(); setRequesting(retestFrom(r, lines)); }}>
                              Request retest
                            </button>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>
            ) : (
              <p className={WHY} data-testid="tr-empty">No test requests in this bucket.</p>
            )}
          </div>
        ) : null}
      </div>

      {requesting ? (
        <RequestTestDialog
          projectId={projectId}
          origin={requesting}
          test={{ name: requesting.test_name, standard: requesting.standard, acceptance: requesting.acceptance }}
          retest={requesting.why === 'failed'
            ? { retest_of: requesting.retest_of, number: requesting.previous_tr?.request_number }
            : null}
          reasonRequired={!!requesting.retest_reason_required}
          onClose={() => setRequesting(null)}
          onIssued={(created, labName) => {
            setRequesting(null);
            toast.success(`${created?.request_number || 'Test request'} issued${labName ? ` to ${labName}` : ''}`);
            if (created?.id) openTr(created.id); else load();
          }}
        />
      ) : null}
    </Layout>
  );
}
